import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Grid, Typography } from '@material-ui/core';
import { AddShoppingCart } from '@material-ui/icons';
import { subMonths, isBefore } from 'date-fns';
import { RootStoreContext } from '../../global/storeContext';
import { ICreditReport } from 'realhaus-sdk';
import {
    CreditRating,
    CreditReportAddresses,
    CreditReportEmployees,
    CreditReportCollection,
    CreditReportPublicRecord,
    CreditAccountSummary,
    CreditAccountSummaryV2,
    CreditRatingV2,
} from './creditReport';
import { PaymentCheckout } from '../wallet/billPaymentCheckout';
import { useStyles } from './styles';

type CreditScreeningComponentProps = {
    rentApplicationId: string;
    tenantId: string;
};

enum CreditReportStatus {
    INPROGRESS = 'IN_PROGRESS',
}

export const CreditScreeningComponent: React.FC<CreditScreeningComponentProps> = observer(
    ({ tenantId, rentApplicationId }) => {
        const { creditReportStore } = useContext(RootStoreContext);
        const [creditReport, setCreditReport] = useState<ICreditReport>();
        useEffect(() => {
            if (!!tenantId) {
                creditReportStore.subscribeToCreditReport(tenantId, (cr) => {
                    if (cr && cr.publicRecords === undefined) {
                        cr.publicRecords = {
                            bankruptcies: [],
                            collections: [],
                            judgement: [],
                            legalItems: []
                        }
                    }

                    setCreditReport(cr);
                });
            }
        }, [tenantId]);

        const handleRequestCreditReport = async () => {
            // send request to generate credit report
            return (await creditReportStore.generateCreditReport(rentApplicationId)) ?? false;
        };

        const creditReportReady =
            creditReport && (creditReport as any).status !== CreditReportStatus.INPROGRESS;

        const crVersion = creditReport?.version;

        return (
            <>
                <Grid container spacing={3}>
                    {/* Add Header to generate credit report or purchase seats */}
                    <Grid item xs={12}>
                        <CreditReportScreeningHeader
                            creditReport={creditReport}
                            onRequestCreditReport={handleRequestCreditReport}
                        />
                    </Grid>
                    {/* Show Credit Report if available */}
                    {creditReportReady && (
                        <>
                            {crVersion === 'v2' ? (
                                <CreditReportScreeningV2 creditReport={creditReport} />
                            ) : (
                                <CreditReportScreeningV1 creditReport={creditReport} />
                            )}
                        </>
                    )}
                </Grid>
            </>
        );
    },
);

const CreditReportScreeningV1: React.FC<{
    creditReport: ICreditReport;
}> = observer(({ creditReport }) => {
    return (
        <>
            <Grid item xs={12}>
                <CreditRating creditReport={creditReport} />
            </Grid>
            {/* Addresses */}
            <Grid item xs={12}>
                <CreditReportAddresses creditReport={creditReport} />
            </Grid>

            {/* Employers */}
            <Grid item xs={12}>
                <CreditReportEmployees creditReport={creditReport} />
            </Grid>

            {/* Collection */}
            <Grid item xs={12}>
                <CreditReportCollection creditReport={creditReport} />
            </Grid>

            {/* Public Records */}
            <Grid item xs={12}>
                <CreditReportPublicRecord creditReport={creditReport} />
            </Grid>

            {/* Credit Account Summary */}
            <Grid item xs={12}>
                <CreditAccountSummary creditReport={creditReport} />
            </Grid>
        </>
    );
});

const CreditReportScreeningV2: React.FC<{
    creditReport: ICreditReport;
}> = observer(({ creditReport }) => {
    return (
        <>
            <Grid item xs={12}>
                <CreditRatingV2 creditReport={creditReport} />
            </Grid>

            {/* Addresses */}
            <Grid item xs={12}>
                <CreditReportAddresses creditReport={creditReport} />
            </Grid>

            {/* Employers */}
            <Grid item xs={12}>
                <CreditReportEmployees creditReport={creditReport} />
            </Grid>

            {/* Collection */}
            <Grid item xs={12}>
                <CreditReportCollection creditReport={creditReport} />
            </Grid>

            {/* Public Records */}
            <Grid item xs={12}>
                <CreditReportPublicRecord creditReport={creditReport} />
            </Grid>

            {/* Credit Account Summary */}
            {/* <Grid item xs={12}>
                <CreditAccountSummaryV2 creditReport={creditReport} />
            </Grid> */}

            {/* Credit Account Summary */}
            <Grid item xs={12}>
                <CreditAccountSummary creditReport={creditReport} />
            </Grid>
        </>
    );
});

const CreditReportScreeningHeader: React.FC<{
    creditReport?: ICreditReport;
    onRequestCreditReport: () => Promise<boolean>;
}> = observer(({ creditReport, onRequestCreditReport }) => {
    const { creditReportStore, userStore } = useContext(RootStoreContext);
    const [availableCRSeats, setAvailableCRSeats] = useState<number>(0);
    const [openCheckout, setOpenCheckout] = React.useState<boolean>(false);
    const classes = useStyles();

    const hasCreditReportSeats = availableCRSeats > 0;
    const creditReportReady =
        creditReport && (creditReport as any).status !== CreditReportStatus.INPROGRESS;
    const creditReportInProgress =
        creditReport && (creditReport as any).status === CreditReportStatus.INPROGRESS;
    const creditReportNeedsRefresh =
        creditReportReady && isBefore(new Date(creditReport.dateCreated), subMonths(new Date(), 6));

    useEffect(() => {
        creditReportStore.subscribeToCreditReportSeats(userStore.userId!, (d) => {
            setAvailableCRSeats(d?.seatsAvailable ?? 0);
        });
    }, []);

    const requestCreditReport = async () => {
        const status = await onRequestCreditReport();
        if (status && creditReport) {
            (creditReport as any).status = 'IN_PROGRESS';
        }
    };

    const handleOpenCheckout = () => {
        setOpenCheckout(true);
    };

    const handleCloseCheckout = () => {
        setOpenCheckout(false);
    };

    return (
        <>
            {creditReportNeedsRefresh ? (
                <Grid
                    container
                    style={{
                        padding: '6px 0',
                        marginBottom: 2,
                    }}
                    xs={12}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.actionButtons} align='center'>
                            <Button
                                color='primary'
                                variant='contained'
                                disabled={!hasCreditReportSeats}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    requestCreditReport();
                                }}
                            >
                                Update Credit Report
                            </Button>
                            <Button variant='outlined' color='primary' onClick={handleOpenCheckout}>
                                <AddShoppingCart fontSize='small' />
                                &nbsp; Purchase Seats
                            </Button>
                            <br />
                            <b>{availableCRSeats} seat(s)</b> available.
                        </Typography>
                    </Grid>
                </Grid>
            ) : creditReportInProgress ? (
                <>
                    <Grid
                        container
                        style={{
                            padding: '6px 0',
                            marginBottom: 2,
                        }}
                        xs={12}
                        justifyContent='center'
                    >
                        <Grid item xs={8}>
                            <Typography
                                variant='h5'
                                style={{ marginTop: '15px', marginBottom: '15px' }}
                                align='center'
                            >
                                Credit report generation in progress, please wait ...
                            </Typography>
                        </Grid>
                    </Grid>
                </>
            ) : (
                !creditReport && (
                    <>
                        {/* No Credit Report, show button to generate a credit report */}
                        <Grid
                            container
                            style={{
                                padding: '6px 0',
                                marginBottom: 2,
                            }}
                            xs={12}
                            justifyContent='center'
                        >
                            <Grid item xs={8}>
                                <Typography
                                    variant='h5'
                                    style={{ marginTop: '15px', marginBottom: '15px' }}
                                    align='center'
                                >
                                    Request a Credit Report
                                </Typography>
                                <Typography variant='body1' style={{ marginBottom: '15px' }}>
                                    You'll get a credit score and full credit history data which is
                                    an effective way to determine a tenant’s financial status and
                                    payment history.
                                </Typography>
                                <Typography
                                    align='center'
                                    style={{ marginBottom: '10px' }}
                                    className={classes.actionButtons}
                                >
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        disabled={!hasCreditReportSeats}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            requestCreditReport();
                                        }}
                                        style={{
                                            paddingLeft: '3rem',
                                            paddingRight: '3rem',
                                        }}
                                    >
                                        Request Credit Report
                                    </Button>

                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={handleOpenCheckout}
                                    >
                                        <AddShoppingCart fontSize='small' />
                                        &nbsp; Purchase Seats
                                    </Button>
                                </Typography>
                                <Typography
                                    variant='subtitle1'
                                    align='center'
                                    style={{ marginBottom: '10px' }}
                                >
                                    You have <b>{availableCRSeats} seat(s)</b> available.
                                    <br />
                                    Requesting a credit report costs 1 seat.
                                </Typography>
                            </Grid>
                        </Grid>
                    </>
                )
            )}

            {openCheckout && (
                <PaymentCheckout
                    open={openCheckout}
                    handleClose={handleCloseCheckout}
                    handleComplete={() => {
                        console.log('Credit Report Seat Purchase Successful');
                    }}
                    handleFailure={() => {
                        console.log('Credit Report Seat Purchase Failed');
                    }}
                    type='CREDIT_REPORT_SEAT'
                />
            )}
        </>
    );
});
